import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, ScanFace, ShieldCheck, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { useAppLock } from "@/contexts/AppLockContext";
import { biometricAvailable, registerBiometric } from "@/lib/webauthn";

const RELOCK_OPTIONS = [
  { value: "0", label: "Immediately" },
  { value: "1", label: "After 1 minute" },
  { value: "5", label: "After 5 minutes" },
  { value: "15", label: "After 15 minutes" },
  { value: "-1", label: "Only when reopened" },
];

export default function AppLockSettings() {
  const { status, refresh } = useAppLock();
  const [bioSupported, setBioSupported] = useState(false);
  const [showPinForm, setShowPinForm] = useState(false);
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    refresh();
    biometricAvailable().then(setBioSupported);
  }, [refresh]);

  const pinSet = !!status?.pin_set;
  const bioCount = status?.biometric_count || 0;

  const savePin = async () => {
    if (!/^\d{4,6}$/.test(pin)) return toast.error("Passcode must be 4 to 6 digits.");
    if (pin !== confirm) return toast.error("The two passcodes don't match.");
    setBusy(true);
    try {
      await api.setAppPin(pin);
      toast.success(pinSet ? "Passcode updated" : "App Lock turned on");
      setShowPinForm(false);
      setPin("");
      setConfirm("");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save passcode");
    } finally {
      setBusy(false);
    }
  };

  const turnOff = async () => {
    setBusy(true);
    try {
      await api.disableAppLock();
      toast.success("App Lock turned off");
      await refresh();
    } catch {
      toast.error("Couldn't turn off App Lock");
    } finally {
      setBusy(false);
    }
  };

  const setupBiometric = async () => {
    setBusy(true);
    try {
      await registerBiometric();
      toast.success("Face ID / Touch ID enabled");
      await refresh();
    } catch (e) {
      const s = e.response?.status;
      toast.error(s ? (formatApiError(e.response?.data?.detail) || "Biometric setup failed") : "Biometric setup was cancelled");
    } finally {
      setBusy(false);
    }
  };

  const removeBiometric = async () => {
    setBusy(true);
    try {
      await api.removeBiometric();
      toast.success("Biometric unlock removed");
      await refresh();
    } catch {
      toast.error("Couldn't remove biometric");
    } finally {
      setBusy(false);
    }
  };

  const changeRelock = async (v) => {
    try {
      await api.setRelock(Number(v));
      await refresh();
      toast.success("Auto-lock timing updated");
    } catch {
      toast.error("Couldn't update timing");
    }
  };

  return (
    <div className="space-y-3" data-testid="app-lock-settings">
      {/* Passcode */}
      <div className="rounded-lg border border-zinc-800 bg-zinc-900 px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <Lock className={`h-4 w-4 ${pinSet ? "text-emerald-300" : "text-zinc-500"}`} />
            <span className="text-sm text-zinc-200">{pinSet ? `Passcode on (${status?.pin_len} digits)` : "Passcode off"}</span>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setShowPinForm((v) => !v)} disabled={busy}
              className="border-zinc-700 bg-transparent text-zinc-200 hover:bg-zinc-800" data-testid="applock-set-pin-btn">
              {pinSet ? "Change" : "Set passcode"}
            </Button>
            {pinSet && (
              <Button size="sm" variant="outline" onClick={turnOff} disabled={busy}
                className="border-red-500/40 bg-transparent text-red-300 hover:bg-red-500/10" data-testid="applock-disable-btn">
                Turn off
              </Button>
            )}
          </div>
        </div>
        {showPinForm && (
          <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="applock-pin-form">
            <Input inputMode="numeric" type="password" maxLength={6} placeholder="4–6 digit passcode" value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
              className="w-[160px] border-zinc-800 bg-zinc-950 focus-visible:ring-[#E0575B]/70" data-testid="applock-pin-input" />
            <Input inputMode="numeric" type="password" maxLength={6} placeholder="Confirm" value={confirm}
              onChange={(e) => setConfirm(e.target.value.replace(/\D/g, ""))}
              className="w-[130px] border-zinc-800 bg-zinc-950 focus-visible:ring-[#E0575B]/70" data-testid="applock-pin-confirm" />
            <Button size="sm" onClick={savePin} disabled={busy} className="bg-[#9B2226] text-white hover:bg-[#7A1A1E]" data-testid="applock-pin-save-btn">Save</Button>
            <button onClick={() => setShowPinForm(false)} className="text-zinc-500 hover:text-zinc-300"><X className="h-4 w-4" /></button>
          </div>
        )}
      </div>

      {/* Biometric */}
      {bioSupported && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-zinc-800 bg-zinc-900 px-4 py-3">
          <div className="flex items-center gap-2.5">
            <ScanFace className={`h-4 w-4 ${bioCount ? "text-emerald-300" : "text-zinc-500"}`} />
            <span className="text-sm text-zinc-200">{bioCount ? "Face ID / Touch ID on" : "Face ID / Touch ID off"}</span>
          </div>
          {bioCount ? (
            <Button size="sm" variant="outline" onClick={removeBiometric} disabled={busy}
              className="border-red-500/40 bg-transparent text-red-300 hover:bg-red-500/10" data-testid="applock-bio-remove-btn">Remove</Button>
          ) : (
            <Button size="sm" variant="outline" onClick={setupBiometric} disabled={busy || !pinSet}
              className="border-zinc-700 bg-transparent text-zinc-200 hover:bg-zinc-800 disabled:opacity-40" data-testid="applock-bio-setup-btn">
              {pinSet ? "Set up" : "Set passcode first"}
            </Button>
          )}
        </div>
      )}

      {/* Auto-lock timing */}
      {pinSet && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-zinc-800 bg-zinc-900 px-4 py-3">
          <div className="flex items-center gap-2.5">
            <ShieldCheck className="h-4 w-4 text-zinc-500" />
            <span className="text-sm text-zinc-200">Require unlock</span>
          </div>
          <Select value={String(status?.relock_minutes ?? 5)} onValueChange={changeRelock}>
            <SelectTrigger className="w-[180px] border-zinc-800 bg-zinc-950 focus:ring-[#E0575B]/70" data-testid="applock-relock-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border-zinc-800 bg-zinc-950">
              {RELOCK_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value} data-testid={`applock-relock-${o.value}`}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <p className="text-xs text-zinc-500">Always required when the app is fully reopened. Forgot your passcode? Signing in with Google again resets it.</p>
    </div>
  );
}
